import { useCallback } from 'react';
import type { FormInstance } from 'antd';

import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { PPGRow } from '../../../../types/promoMetrics';
import type { ApiPPG } from '../../../../types/promoMetrics';
import { buildPromoPayload } from '../../../../utils/buildPromoPayload';
import { buildChangesPayload } from '../../utils/promoStateUtils';
import { useOptionalRuleStoreApi } from '../../../../rules/store/FieldRulesProvider';
import type { CreatePromoPayload } from '../mutations/useCreatePromoMutation';

interface UsePromoSaveArgs {
    promoForm: FormInstance;
    ppgData: PPGRow[];
    baseline: Map<string, Record<string, number>>;
    planId: string;
    eventId: string;
    savedPromotionId: string | null;
    promoMeta: { promotion_id: string } | null;
    existingPromoFormValues: Record<string, unknown> | null;
    existingPpgData: ApiPPG[];
    isEditMode: boolean;
    createPromo: (payload: CreatePromoPayload) => void;
    resetPpgToExisting: (existingRows: ApiPPG[]) => void;
    clearPpgState: () => void;
    triggerUpliftBeforeSave: () => Promise<boolean>;
    /** Names of fields the rule engine has disabled; excluded from the create-flow reset. */
    disabledFieldNames: Set<string>;
}

interface UsePromoSaveReturn {
    save: () => Promise<void>;
    reset: () => void;
}

const usePromoSave = ({
    promoForm,
    ppgData,
    baseline,
    planId,
    eventId,
    savedPromotionId,
    promoMeta,
    existingPromoFormValues,
    existingPpgData,
    isEditMode,
    createPromo,
    resetPpgToExisting,
    clearPpgState,
    triggerUpliftBeforeSave,
    disabledFieldNames,
}: UsePromoSaveArgs): UsePromoSaveReturn => {
    const { promoFields, entityAttributes } = useTpoGenConfig();
    // Cross-scope (event/plan) rule-store values, for promo fields declaring
    // `valueFrom` (e.g. Danone's promo name sourced from `event:eventName`).
    // Optional: promo can render outside the provider in isolated tests/stories.
    const ruleStore = useOptionalRuleStoreApi();

    const save = useCallback(async () => {
        try {
            await promoForm.validateFields();
        } catch {
            return;
        }

        // Pre-save uplift: fetch latest uplift values before persisting
        const canProceed = await triggerUpliftBeforeSave();
        if (!canProceed) return;

        const fv = promoForm.getFieldsValue(true);
        const externalValues = ruleStore?.getState().values ?? {};
        const fieldsPayload = buildPromoPayload(entityAttributes, promoFields, fv, externalValues);
        const changes = buildChangesPayload(ppgData, baseline);

        createPromo({
            plan_id: planId,
            event_id: eventId,
            promotion_id: savedPromotionId ?? promoMeta?.promotion_id ?? null,
            ...fieldsPayload,
            ppg_ids: ppgData.map((p) => p.ppg_id),
            changes,
            user_id: localStorage.getItem('email') ?? '',
        } as CreatePromoPayload);
    }, [
        promoForm,
        ppgData,
        baseline,
        planId,
        eventId,
        createPromo,
        savedPromotionId,
        promoMeta,
        triggerUpliftBeforeSave,
        promoFields,
        entityAttributes,
        ruleStore,
    ]);

    const reset = useCallback(() => {
        if (isEditMode && existingPromoFormValues) {
            promoForm.resetFields();
            promoForm.setFieldsValue(existingPromoFormValues);
            resetPpgToExisting(existingPpgData);
        } else {
            // Create flow: reset only enabled fields. Disabled fields carry values
            // derived from other scopes (e.g. event dates) and have no initialValue,
            // so resetFields() would blank them — skip them to preserve their values.
            const namesToReset = Object.keys(promoForm.getFieldsValue(true)).filter(
                (name) => !disabledFieldNames.has(name),
            );
            promoForm.resetFields(namesToReset);
            clearPpgState();
        }
    }, [
        isEditMode,
        existingPromoFormValues,
        existingPpgData,
        promoForm,
        resetPpgToExisting,
        clearPpgState,
        disabledFieldNames,
    ]);

    return { save, reset };
};

export default usePromoSave;
